import { cn } from '@/lib/utils'
import type { CalendarEvent, CalendarResource, PaymentStatus } from '@/lib/render'

import type { CalendarViewProps, TileMoveGeometry } from './shared'
import { EventShell } from './BookingActions'
import {
  AttendanceMark,
  CalendarNoteLayer,
  ClosedTimeLayer,
  columnOverlays,
  EmptyState,
  PaymentMark,
  deriveResources,
  durationMins,
  eventBelongsToResource,
  eventMatchesAnyResource,
  formatMinutesLabel,
  getDatePart,
  getHours,
  minutesFromMidnight,
  parseHour,
  SlotBandLayer,
  statusColor,
  statusPrefix,
} from './shared'

const COLUMN_WIDTH = 220
const TIME_COL_WIDTH = 54
// 2.25px/min — a 30-minute slot is ~68px (double the prior half-hour block) so each
// card has room for customer + service + duration. Kept in step with MIN_EVENT_HEIGHT.
const PIXELS_PER_MINUTE = 2.25
// Floor for every event block so short appointments still fit customer + service + duration.
const MIN_EVENT_HEIGHT = 64

interface MachineColumn {
  resource: CalendarResource
  events: CalendarEvent[]
}

interface PositionedEvent extends CalendarEvent {
  lane: number
  lanes: number
}

// One column per machine (resource). Columns are ordered by the resource's
// configured display_order (lowest first), falling back to name so the layout is
// deterministic even when order is unset. Events with no resource land in a
// trailing "Unassigned" column. Within a column, events are sorted by start time.
//
// `includeEmpty` keeps machines with nothing booked. Off, a quiet chat calendar
// stays narrow; on (the /schedule screen), every machine gets a column — an idle
// machine with no column is a machine you cannot click to book into, which is
// most of the point of click-to-book.
function groupByMachine(
  events: CalendarEvent[],
  resources: CalendarResource[],
  includeEmpty = false,
): MachineColumn[] {
  const orderOf = (r: CalendarResource) => (typeof r.display_order === 'number' ? r.display_order : Number.POSITIVE_INFINITY)
  const sorted = [...resources].sort((a, b) => orderOf(a) - orderOf(b) || a.name.localeCompare(b.name))
  const columns: MachineColumn[] = sorted.map(resource => ({
    resource,
    events: events.filter(event => eventBelongsToResource(event, resource)),
  }))

  const unassigned = resources.length
    ? events.filter(event => !eventMatchesAnyResource(event, resources))
    : events
  // "Unassigned" is only ever a home for events that HAVE nowhere else to go —
  // never created speculatively, even with includeEmpty. Two reasons: a service
  // needing no machine can be booked from any column via "Show all services", so
  // an always-present empty column is pure clutter; and a tenant with NO
  // resources configured cannot take a booking at all (setup is incomplete, and
  // the API refuses with setup_required), so an inviting empty grid would be a
  // promise the dialog cannot keep. No columns then means EmptyState, which is
  // the honest answer.
  if (unassigned.length > 0) {
    columns.push({ resource: { id: 'unassigned', name: 'Unassigned' }, events: unassigned })
  }

  return includeEmpty ? columns : columns.filter(col => col.events.length > 0)
}

// Assign overlapping events within a single machine column to side-by-side lanes
// so two bookings that somehow overlap on the same machine never draw on top of
// each other.
function positionOverlappingEvents(events: CalendarEvent[]): PositionedEvent[] {
  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start))
  const laneEnds: number[] = []

  return sorted.map(event => {
    const start = minutesFromMidnight(event.start)
    const end = minutesFromMidnight(event.end)
    let lane = laneEnds.findIndex(laneEnd => laneEnd <= start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(end)
    } else {
      laneEnds[lane] = end
    }

    const concurrent = sorted.filter(other =>
      minutesFromMidnight(other.start) < end && minutesFromMidnight(other.end) > start,
    ).length

    return { ...event, lane, lanes: Math.max(concurrent, laneEnds.length) }
  })
}

function MachineEventCard({
  event,
  dayStart,
  interactive,
  onChanged,
  move,
}: {
  event: PositionedEvent
  dayStart: number
  interactive?: boolean
  onChanged?: () => void
  move?: TileMoveGeometry
}) {
  const color = statusColor(event)
  const cancelled = event.status === 'cancelled'
  const top = Math.max(0, (minutesFromMidnight(event.start) - dayStart) * PIXELS_PER_MINUTE)
  const height = Math.max(MIN_EVENT_HEIGHT, durationMins(event.start, event.end) * PIXELS_PER_MINUTE - 4)
  // Percentages of the column, so a fixed column and a stretched one lay out alike.
  const gutter = 4
  const inset = 16 + gutter * Math.max(0, event.lanes - 1)
  const laneWidth = `((100% - ${inset}px) / ${event.lanes})`
  const width = `calc${laneWidth}`
  const left = `calc(8px + ${event.lane} * (${laneWidth} + ${gutter}px))`

  return (
    // EventShell carries the absolute positioning so an interactive tile occupies
    // exactly the same box as a plain one — the grid layout is identical either
    // way. Non-interactive tiles keep the click-through link to the booking.
    <EventShell
      event={event}
      interactive={interactive}
      onChanged={onChanged}
      move={move}
      className={cn(
        'absolute flex flex-col overflow-hidden rounded-sm border border-l-4 border-border bg-card px-2 py-[5px]',
        cancelled && 'opacity-60',
      )}
      style={{ borderLeftColor: color, top, height, left, width }}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground', cancelled && 'line-through')}>{event.customer_name ?? 'Booking'}</span>
        <div className="flex items-center gap-[5px]">
          <AttendanceMark event={event} />
          {event.payment_status ? <PaymentMark status={event.payment_status as PaymentStatus} byPackage={event.paid_with_package} /> : null}
        </div>
      </div>
      {event.service_name ? (
        <span className={cn('mt-0.5 truncate text-[11px] text-foreground/80', cancelled && 'line-through')}>{event.service_name}</span>
      ) : null}
      <span className={cn('mt-px truncate text-[11px] text-muted-foreground', cancelled && 'line-through')}>
        {statusPrefix(event)}{durationMins(event.start, event.end)}min
      </span>
    </EventShell>
  )
}

export function DayCalendarView({ data, interactive, onChanged, onPickSlot, onMoveBooking, showAllResources, hideDateHeading, fill }: CalendarViewProps) {
  const events = (data.events ?? [])
    .filter(event => getDatePart(event.start) === data.date)
    .sort((a, b) => a.start.localeCompare(b.start))

  const dateLabel = new Date(`${data.date}T00:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const resources = deriveResources(events, data.resources)
  const columns = groupByMachine(events, resources, showAllResources)

  // A day with nothing on it still needs a grid when this surface can book —
  // "No appointments" with nothing to click is exactly the wrong answer to a
  // wide-open day. With no columns to draw there is still nothing to show.
  if (columns.length === 0 || (events.length === 0 && !showAllResources)) {
    return (
      <div>
        {hideDateHeading ? null : (
          <h3 className="border-b border-border bg-muted px-4 py-2.5 text-[14px] font-bold text-foreground">{dateLabel}</h3>
        )}
        <EmptyState />
      </div>
    )
  }

  const hours = getHours(data)
  const dayStart = parseHour(data.business_hours?.open, hours[0] ?? 8) * 60
  const dayEnd = parseHour(data.business_hours?.close, hours[hours.length - 1] ?? 20) * 60
  // Half-hour grid marks (minutes from midnight) across the open window.
  const marks: number[] = []
  for (let m = dayStart; m <= dayEnd; m += 30) marks.push(m)
  const gridHeight = Math.max(540, (dayEnd - dayStart) * PIXELS_PER_MINUTE)

  // Drag-to-move geometry, built once per render rather than per tile. The scale
  // is THIS grid's — the resource view draws at 1.5px/min, so sharing a constant
  // between them would land a drop on the wrong slot in one of the two.
  const move: TileMoveGeometry | undefined = onMoveBooking
    ? {
        pixelsPerMinute: PIXELS_PER_MINUTE,
        intervalMinutes: data.slot_minutes ?? 30,
        dayStartMinutes: dayStart,
        dayEndMinutes: dayEnd,
        date: data.date,
        onMoveBooking,
      }
    : undefined

  // Filling, every column grows to share the width but never below its minimum;
  // past that the grid scrolls sideways as before.
  const columnStyle = fill ? { flex: '1 1 0', minWidth: COLUMN_WIDTH } : { width: COLUMN_WIDTH }

  return (
    <div className={cn(fill && 'flex min-h-0 flex-col')}>
      {hideDateHeading ? null : (
        <h3 className="border-b border-border bg-muted px-4 py-2.5 text-[14px] font-bold text-foreground">{dateLabel}</h3>
      )}
      {/* ONE scroll container for both axes. Sticky answers only to the NEAREST
          scrolling ancestor, so with the old split (horizontal outside, vertical
          inside) a sticky-left time column would ignore the horizontal scroller
          entirely — both scrollbars must live on the same element for the time
          gutter and the machine-name header to freeze. */}
      <div className={cn('overflow-auto', fill ? 'min-h-0' : 'max-h-[640px]')}>
        <div
          className={fill ? undefined : 'w-max'}
          style={fill ? { minWidth: TIME_COL_WIDTH + columns.length * COLUMN_WIDTH } : undefined}
        >
          <div className="sticky top-0 z-30 flex border-b border-border bg-card">
            {/* Corner cell: sticky on the left as well, so neither scroll axis
                moves it and machine headers slide beneath it. */}
            <div className="sticky left-0 z-10 shrink-0 bg-card" style={{ width: TIME_COL_WIDTH }} />
            {columns.map(col => (
              <div
                key={col.resource.id}
                className="flex shrink-0 items-center justify-between gap-1.5 border-l border-border bg-muted px-2.5 py-2"
                style={columnStyle}
              >
                <span className="line-clamp-2 min-w-0 flex-1 text-[13px] font-bold text-foreground">{col.resource.name}</span>
                <span className="min-w-[22px] shrink-0 overflow-hidden rounded-lg bg-border px-1.5 py-px text-center text-[12px] font-bold text-muted-foreground">{col.events.length}</span>
              </div>
            ))}
          </div>
          <div className="relative flex bg-card" style={{ height: gridHeight }}>
            {/* Grid lines first so they paint UNDER the event cards, never across them. */}
            {/* z-0 (columns sit at z-10) so the grid never draws across an event card —
                sibling order alone is not enough on Android. */}
            {marks.map(mark => (
              <div
                key={`line-${mark}`}
                className={cn('pointer-events-none absolute z-0 h-px bg-border', mark % 60 !== 0 && 'bg-muted')}
                style={{
                  top: (mark - dayStart) * PIXELS_PER_MINUTE,
                  left: TIME_COL_WIDTH,
                  ...(fill ? { right: 0 } : { width: columns.length * COLUMN_WIDTH }),
                }}
              />
            ))}
            {/* z-20 beats the z-10 columns so event cards slide UNDER the
                frozen gutter; the opaque bg-card is what hides them. */}
            <div className="sticky left-0 z-20 shrink-0 bg-card" style={{ width: TIME_COL_WIDTH, height: gridHeight }}>
              {marks.map(mark => {
                const onHour = mark % 60 === 0
                return (
                  <span
                    key={mark}
                    className={cn(
                      'absolute right-2 whitespace-nowrap text-[10px] text-muted-foreground',
                      !onHour && 'text-[9px] text-muted-foreground/70',
                    )}
                    style={{ top: (mark - dayStart) * PIXELS_PER_MINUTE - 7 }}
                  >
                    {formatMinutesLabel(mark)}
                  </span>
                )
              })}
            </div>
            {columns.map((col, colIndex) => {
              const positioned = positionOverlappingEvents(col.events)
              const overlays = columnOverlays(data, col.resource, colIndex === 0)
              return (
                <div
                  key={col.resource.id}
                  className="relative z-10 shrink-0 border-l border-border"
                  style={{ ...columnStyle, height: gridHeight }}
                >
                  {/* Closed time first of all: it is the backdrop, and it must
                      never sit on top of a booking (a block and a booking can
                      legitimately overlap when the block was added later). */}
                  <ClosedTimeLayer
                    ranges={overlays.blocked}
                    labels={overlays.blockLabels}
                    dayStartMinutes={dayStart}
                    pixelsPerMinute={PIXELS_PER_MINUTE}
                  />
                  {/* Bands next so the event cards paint on top of them — see
                      SlotBandLayer. A click on a booking must reach the tile. */}
                  {onPickSlot ? (
                    <SlotBandLayer
                      date={data.date}
                      resource={col.resource}
                      events={col.events}
                      blocked={overlays.blocked}
                      dayStartMinutes={dayStart}
                      dayEndMinutes={dayEnd}
                      intervalMinutes={data.slot_minutes ?? 30}
                      pixelsPerMinute={PIXELS_PER_MINUTE}
                      onPickSlot={onPickSlot}
                    />
                  ) : null}
                  {positioned.map(event => (
                    <MachineEventCard
                      key={`${col.resource.id}-${event.id}`}
                      event={event}
                      dayStart={dayStart}
                      interactive={interactive}
                      onChanged={onChanged}
                      move={move}
                    />
                  ))}
                  {/* Memos on top so they stay readable over a card, but with
                      pointer events off so the time underneath stays clickable
                      — a memo that swallowed the click would make the slot
                      unbookable in the UI while the API still sold it. */}
                  <CalendarNoteLayer
                    notes={overlays.notes}
                    dayStartMinutes={dayStart}
                    pixelsPerMinute={PIXELS_PER_MINUTE}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
